import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { use100vh } from "react-div-100vh";
import { Link } from "react-router-dom";
import WorkoutSelect from "../BasicSelect/WorkoutSelect";

interface PreviousSessionsDetailsDrawerProps {
  allSessionsOfExercise: any[];
  currentSessionIndex: number;
  setCurrentSessionIndex: (index: number) => void;
  isOpen: boolean;
  onClose: () => void;
}

const PreviousSessionsDetailsDrawer: React.FC<
  PreviousSessionsDetailsDrawerProps
> = ({
  allSessionsOfExercise,
  currentSessionIndex,
  setCurrentSessionIndex,
  isOpen,
  onClose,
}) => {
  const windowHeight = use100vh();
  const selectedExerciseSession = allSessionsOfExercise[currentSessionIndex];
  const selectedSessionFullWorkout = selectedExerciseSession.parentWorkout;

  const handleWorkoutSelected = (newSelectedWorkout: Workout) => {
    const newSelectedSessionIndex = allSessionsOfExercise.findIndex(
      (exerciseSession) =>
        exerciseSession.parentWorkout.id === newSelectedWorkout.id
    );

    setCurrentSessionIndex(newSelectedSessionIndex);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100 transform-none"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-t-2xl bg-white text-left shadow-xl transition-all w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <button
                    type="button"
                    className="absolute right-4"
                    onClick={onClose}
                  >
                    <XMarkIcon
                      className="h-6 w-6 text-gray-900"
                      aria-hidden="true"
                    />
                  </button>
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 max-w-fit"
                  >
                    Last Time You Did
                    <br />
                    <span className="text-indigo-500">
                      {selectedExerciseSession.name}
                    </span>
                    :
                  </Dialog.Title>

                  <WorkoutSelect
                    id="previous-workouts-with-exercise-select"
                    className="my-2"
                    initialSelectedWorkout={selectedSessionFullWorkout.id}
                    workouts={allSessionsOfExercise.map((exerciseSession) => {
                      return exerciseSession.parentWorkout;
                    })}
                    onSelect={handleWorkoutSelected}
                  />
                  <Link
                    to={`/workout/${selectedSessionFullWorkout.id}`}
                    state={{
                      workoutTitle: selectedSessionFullWorkout.title,
                      workoutDate: selectedSessionFullWorkout.date,
                    }}
                    className="text-sm text-indigo-500 font-medium underline"
                  >
                    Go to workout
                  </Link>
                  <hr className="mt-3 border-t-2 border-gray-200"></hr>
                  {selectedSessionFullWorkout.exercises && (
                    <div
                      className="mt-3 flex flex-col gap-y-3 overflow-y-auto"
                      style={{
                        maxHeight: `calc(${windowHeight}px - 280px)`,
                      }}
                    >
                      {selectedSessionFullWorkout.exercises.map(
                        (exercise: Exercise, exerciseIndex: number) => {
                          if (exercise.sets.length > 0) {
                            return (
                              <div
                                key={exerciseIndex}
                                className={
                                  exercise.name === selectedExerciseSession.name
                                    ? `font-medium text-sm text-indigo-500`
                                    : "text-sm text-gray-500"
                                }
                              >
                                <p
                                  className={
                                    exercise.name ===
                                    selectedExerciseSession.name
                                      ? `font-bold`
                                      : " font-medium"
                                  }
                                >
                                  {exercise.name || "Untitled Exercise"}
                                </p>
                                <p className="flex flex-wrap">
                                  {exercise.sets.map((set, index) => {
                                    return (
                                      <span key={index}>
                                        <b
                                          className={
                                            exercise.name ===
                                            selectedExerciseSession.name
                                              ? `font-bold text-indigo-500`
                                              : " font-medium"
                                          }
                                        >
                                          {index + 1}.
                                        </b>
                                        {"\xa0"}
                                        {!set.weight ? 0 : set.weight}x
                                        {!set.reps ? 0 : set.reps}
                                        {"\xa0\xa0\xa0"}
                                      </span>
                                    );
                                  })}
                                </p>
                                {exercise.name ===
                                  selectedExerciseSession.name &&
                                  exercise.notes && (
                                    <>
                                      <p className="font-bold">Notes:</p>
                                      <p className="font-medium whitespace-pre-line">
                                        {exercise.notes}
                                      </p>
                                    </>
                                  )}
                              </div>
                            );
                          }
                          return (
                            <div
                              key={exerciseIndex}
                              className={
                                exercise.name === selectedExerciseSession.name
                                  ? `font-medium text-sm text-indigo-500`
                                  : "text-sm text-gray-500"
                              }
                            >
                              <p
                                className={
                                  exercise.name === selectedExerciseSession.name
                                    ? `font-bold`
                                    : " font-medium"
                                }
                              >
                                {exercise.name || "Untitled Exercise"}
                              </p>
                              <p>No set data for this exercise</p>
                            </div>
                          );
                        }
                      )}
                      {selectedSessionFullWorkout.notes && (
                        <>
                          <hr className="mt-3 border-t-2 border-gray-200"></hr>

                          <div className="text-sm text-gray-500">
                            <p className="font-medium">Workout Notes</p>
                            <p className="whitespace-pre-line">
                              {selectedSessionFullWorkout.notes}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="bg-gray-50 px-4 py-3">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    Ok
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PreviousSessionsDetailsDrawer;
